import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from "@mui/material";
import { Link, useParams, useRouteLoaderData } from "react-router-dom";

import {
  GCP_WORKFLOW_EXECUTION_UI_TABLE_COLUMNS,
} from "constants";
import EmptyStatePlaceholder from "components/EmptyStatePlaceholder";
import GcpWorkflowExecutionsTable from "components/GcpWorkflowExecutionsTable";
import PageHeader from "components/PageHeader";
import InlineMessage from "components/InlineMessage";
import buildRoute from "../components/buildRoute";
import getCurrentUser from "hooks/getCurrentUser";
import getConfig from "hooks/getConfig";
import { hasWriteAccess } from "utils/userPermissions";


function MatchingJobsIndexPage() {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { projectId } = useParams();
  const loggedInUser = getCurrentUser()
  // get matching configs from active Parter
  const activePartner = loggedInUser.partners.filter(partner => partner.gcp_project_id === projectId)[0]

  // Use project-specific feature flags from loader, fall back to global FLAGS
  const { activeProjectDetails } = useRouteLoaderData("projectRoot");
  const projectFlags = activeProjectDetails?.featureFlags || {};
  const { FLAGS: globalFlags } = getConfig();
  const FLAGS = Object.keys(projectFlags).length > 0 ? projectFlags : globalFlags;

  const targetsmartMatchingEnabled = FLAGS?.TARGETSMART_MATCHING;
  const catalistMatchingEnabled = FLAGS?.CATALIST_MATCHING;
  const areBothMatchingProvidersEnabled = targetsmartMatchingEnabled && catalistMatchingEnabled;
  const areNeitherMatchingProvidersEnabled = !targetsmartMatchingEnabled && !catalistMatchingEnabled;
  // Check the active user's roles for conditional rendering
  const userActiveRoles = loggedInUser.roles.filter(role => role.partner_gcp_project_id === projectId)
  // Allow Admin, Engineer, and Editor to create/edit resources
  const userHasWriteAccess = hasWriteAccess(userActiveRoles);

    useEffect(() => {
      const fetchJobs = async () => {
        setIsLoading(true);
        try {
          const queryParams = new URLSearchParams();
          queryParams.append("partner", projectId);
          const response = await fetch(`/matching/api/matching-jobs/?${queryParams.toString()}`);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const jobs = await response.json();
          setJobs(jobs);
        } catch (error) {
          console.error('Error fetching jobs:', error);
        } finally {
          setIsLoading(false);
        }
      }

      fetchJobs();
    }, [projectId]);

  let subtitle =
    "Create and manage jobs to match your data with Catalist voter file data.";

  if (targetsmartMatchingEnabled) {
    subtitle =
      "Create and manage jobs to match your data with TargetSmart voter file data.";
  }

  if (areBothMatchingProvidersEnabled) {
    subtitle =
      "Create and manage jobs to match your data with voter file data from Catalist and TargetSmart.";
  }

  if (areNeitherMatchingProvidersEnabled) {
    subtitle =
      "Create and manage jobs to match your data with voter file data from Catalist or TargetSmart.";
  }

  return (
    <>
      <PageHeader
        title="Voter File Matching Jobs"
        subtitle={subtitle}
        action={
          <>
           {areNeitherMatchingProvidersEnabled || !userHasWriteAccess ?
            null
            :<Button
            component={Link}
            to="new"
            variant="contained"
            color="secondary"
            aria-label="Create New Job"
          >
            Create New Job
          </Button>}
          </>
        }
      />
      {areNeitherMatchingProvidersEnabled ? 
      <InlineMessage
        type="warning"
        message="Matching is not currently enabled for this project. For help setting that up, reach out to us at help@techallies.org"
        sxOverrides={{ mb: 2 }}
      />
      : null}
      <Box component="section">
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : jobs.length === 0 ? (
          <EmptyStatePlaceholder
            title={"No matching jobs to show"}
            subtitle={
              "Once you create your first matching job it will show up here."
            }
          />
        ) : (
          <GcpWorkflowExecutionsTable
            executions={jobs}
            optionalVisibleColumns={
              targetsmartMatchingEnabled && catalistMatchingEnabled
                ? [GCP_WORKFLOW_EXECUTION_UI_TABLE_COLUMNS.VENDOR]
                : []
            }
          />
        )}
      </Box>
    </>
  );
}

const routeArgs = {
  element: <MatchingJobsIndexPage />,
};

export default buildRoute(routeArgs);
